import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { useAuth } from "../contexts/AuthContext";
import { useFieldAccess, useMyFieldAccess, FlacScopeBanner } from "../hooks/useFieldAccess";
import { ArrowLeft, Save, Loader2, Search, Check, UserPlus, ChevronsUpDown, CheckCircle2 } from "lucide-react";
import { cn } from "../lib/utils";
// Iter53 P37 — tab Audit (read-only, self-contained) extras din SaleForm.js
import { SaleFormAuditTab } from "../components/SaleForm/SaleFormAuditTab";
// Iter53 P46 — tab Backoffice extras + Context pentru state sharing
import { SaleFormBackofficeTab } from "../components/SaleForm/SaleFormBackofficeTab";
import { SaleFormContractTab } from "../components/SaleForm/SaleFormContractTab";
import { SaleFormDetailsTab } from "../components/SaleForm/SaleFormDetailsTab";
import { SaleFormProvider } from "../components/SaleForm/SaleFormContext";
import { LinkResolutionDialog } from "../components/SaleForm/LinkResolutionDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const SaleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;
  const isAdminOrBO = user?.role === "admin" || user?.role === "backoffice";
  // Iter40b — FLAC scope
  const scope = useMyFieldAccess();
  const flac = useFieldAccess(scope, { mode: "scope" });

  // Iter43.4 — Tabs state with URL persistence (?tab=contract)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "details";
  const handleTabChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "details") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  // Iter43.4 — FLAC writeability helper used by tab inputs (admin bypass)
  const canWrite = (fieldKey) => {
    if (!scope?.enforced) return true; // admin or FLAC disabled
    const ro = scope?.read_only_fields || [];
    const hidden = scope?.hidden_fields || [];
    return !ro.includes(fieldKey) && !hidden.includes(fieldKey);
  };

  // Iter40c — FLAC visibility helper: row is hidden if FLAC declares the field hidden.
  // Mirrors `useFlacField()` logic without per-field hook wiring.
  const isHidden = (fieldKey) => {
    if (!scope?.enforced) return false; // admin or FLAC disabled
    const hidden = scope?.hidden_fields || [];
    return hidden.includes(fieldKey);
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [commercialAgents, setCommercialAgents] = useState([]);
  const [distributionZones, setDistributionZones] = useState({ electric: [], gas: [] });
  const [salesChannels, setSalesChannels] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Iter67d — oferte (offer_id/product_kind obligatorii la vânzare NOUĂ; nu la reutilizarea unui contract existent)
  const [offers, setOffers] = useState([]);
  const [linkPrompt, setLinkPrompt] = useState(null); // 409 multiple_sales / no_sale_for_contract → rezolvare explicită
  const commodityTouchedRef = useRef(false);
  
  // Client search
  const [showNewClient, setShowNewClient] = useState(true);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);
  // Iter48 — Dirty tracking + saved flash for EDIT mode
  const lastSavedSnapshotRef = useRef(null);
  const [justSaved, setJustSaved] = useState(false);
  
  // Combined form data for quick-sale (creates client + contract + sale)
  const [formData, setFormData] = useState({
    // Client fields
    client_type: "PF",
    cnp: "",
    cui: "",
    first_name: "",
    last_name: "",
    company_name: "",
    phone_mobile: "",
    email: "",
    county: "",
    locality: "",
    street: "",
    // Contract fields
    pod: "",
    nlc: "",
    commodity: "electric",
    product_name: "",
    distribution_zone: "",
    estimated_annual_kwh: "",
    signed_date: "",
    previous_supplier: "",
    // Sale fields
    channel: "",
    agent_name: user?.name || "",
    agent_code: user?.agent_code || "",
    bo_operator_name: isAdminOrBO ? user?.name : "",
    notes_agent: "",
    // Iter67d — ofertă + identitate pe POD existent
    offer_id: "",
    product_kind: "",
    identity_resolution: "",
    easy_contract_id: "",
    sf_contract_id: "",
    sale_id: "",
    new_sale_purpose: "",
    justification: "",
    operation_key: "",
    // Status
    internal_status: "Draft",
    // Iter43.4 — Contract & Loc consum (paritate EDIT)
    contract_number: "",
    contract_number_old: "",
    commercial_product_name: "",
    contract_type: "",
    customer_category: "",
    offer_type: "",
    raw_product_name: "",
    is_bundle: false,
    contract_duration_months: "",
    price_kwh: "",
    price_kwh_gas: "",
    distributor_name: "",
    contract_date: "",
    start_date: "",
    activation_date: "",
    cancellation_date: "",
    csd_date: "",
    // Loc consum extras
    nlc_gaz: "",
    pdr: "",
    clc: "",
    numar_loc_consum: "",
    tip_loc_consum: "",
    consumption_county: "",
    consumption_locality: "",
    consumption_street: "",
    // Iter43.5 — Backoffice & Workflow SF
    bo_received_at: "",
    problem_reason_code: "",
    requires_rework: false,
    returned: false,
    return_date: "",
    return_reason: "",
    uploaded_flag: false,
    uploaded_at: "",
    qc_call_date: "",
    qc_operator_name: "",
    quality_call_outcome: "",
    cancellation_reason_text: "",
    rejection_subreason: "",
    // Workflow SF — supplier-level (Contract)
    business_status: "",
    supplier_status: "",
    supplier_ok: false,
    supplier_ko: false,
    supplier_status_date: "",
    // Contract status (separate entity contract_status.*)
    salesforce_contract_state: "",
    salesforce_status_date: "",
    cs_cancellation_reason: "",
    cs_cancellation_details: "",
    cs_quality_call_outcome: "",
    cs_quality_call_date: "",
    // Iter43.6 — Audit & Ownership (read-only/disabled system-managed)
    sf_id: "",
    sf_agent_ref_id: "",
    commercial_agent_id: "",
    raw_agent_full_name: "",
    import_source: "",
    import_batch_id: "",
    first_imported_at: "",
    reconciliation_status: "",
    agent_match_confidence: "",
    ownership_source_of_truth: "",
    ownership_override_flag: false,
    activation_pending_since: "",
    crm_internal_id: "",
    external_legacy_id: "",
    sales_origin: "",
    sales_unit_external_name: "",
    last_synced_from_sf_at: "",
    // Iter43.6 — Comisionare (mostly system-set; admin can override)
    commission_month: "",
    commissioned: false,
    commissioned_at: "",
    report_month: "",
    signing_month: "",
    // Iter43.6 — Contract date/agency
    agency: "",
    created_at: "",
  });

  // Debounced client search
  const searchClients = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setClientSearchResults([]);
      return;
    }
    
    setSearchingClients(true);
    try {
      const response = await axios.get(`${API_URL}/clients/search/autocomplete?q=${encodeURIComponent(term)}&limit=10`);
      setClientSearchResults(response.data);
    } catch (error) {
      console.error("Failed to search clients");
    } finally {
      setSearchingClients(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClients(clientSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchTerm, searchClients]);

  // BATCH 3: Duplicate detection inline (POD, CNP/CUI, phone)
  const [duplicates, setDuplicates] = useState({ pod: [], cnp_cui: [], phone: [] });
  const dupTimerRef = useRef(null);
  useEffect(() => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    const pod = (formData.pod || "").trim();
    const cnp = (formData.cnp || "").trim();
    const cui = (formData.cui || "").trim();
    const phone = (formData.phone_mobile || "").trim();
    if (!pod && !cnp && !cui && !phone) {
      setDuplicates({ pod: [], cnp_cui: [], phone: [] });
      return;
    }
    dupTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (pod) params.set("pod", pod);
        if (cnp) params.set("cnp", cnp);
        if (cui) params.set("cui", cui);
        if (phone) params.set("phone", phone);
        if (id) params.set("exclude_sale_id", id);
        const res = await axios.get(`${API_URL}/sales-check-duplicates?${params.toString()}`);
        setDuplicates(res.data?.matches || { pod: [], cnp_cui: [], phone: [] });
      } catch {
        setDuplicates({ pod: [], cnp_cui: [], phone: [] });
      }
    }, 500);
    return () => dupTimerRef.current && clearTimeout(dupTimerRef.current);
  }, [formData.pod, formData.cnp, formData.cui, formData.phone_mobile, id]);

  const fetchSale = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sales/${id}`);
      const sale = response.data;
      
      // Populate form with sale data
      setFormData(prev => ({
        ...prev,
        // Contract data
        pod: sale.contract?.pod || "",
        nlc: sale.contract?.nlc || "",
        commodity: sale.contract?.commodity || "electric",
        product_name: sale.contract?.product_name || "",
        distribution_zone: sale.contract?.distribution_zone || "",
        estimated_annual_kwh: sale.contract?.estimated_annual_kwh?.toString() || "",
        signed_date: sale.contract?.signed_date || "",
        previous_supplier: sale.contract?.previous_supplier || "",
        // Iter43.4 — Contract & Loc consum extras
        contract_number: sale.contract?.contract_number || "",
        contract_number_old: sale.contract?.contract_number_old || "",
        commercial_product_name: sale.contract?.commercial_product_name || "",
        contract_type: sale.contract?.contract_type || "",
        customer_category: sale.contract?.customer_category || "",
        offer_type: sale.contract?.offer_type || "",
        raw_product_name: sale.contract?.raw_product_name || "",
        is_bundle: !!sale.contract?.is_bundle,
        contract_duration_months: sale.contract?.contract_duration_months?.toString() || "",
        price_kwh: sale.contract?.price_kwh?.toString() || "",
        price_kwh_gas: sale.contract?.price_kwh_gas?.toString() || "",
        distributor_name: sale.contract?.distributor_name || "",
        contract_date: sale.contract?.contract_date || "",
        start_date: sale.contract?.start_date || "",
        activation_date: sale.contract?.activation_date || "",
        cancellation_date: sale.contract?.cancellation_date || "",
        csd_date: sale.contract?.csd_date || "",
        nlc_gaz: sale.contract?.nlc_gaz || "",
        pdr: sale.contract?.pdr || "",
        clc: sale.contract?.clc || "",
        numar_loc_consum: sale.contract?.numar_loc_consum || "",
        tip_loc_consum: sale.contract?.tip_loc_consum || "",
        consumption_county: sale.contract?.consumption_county || "",
        consumption_locality: sale.contract?.consumption_locality || "",
        consumption_street: sale.contract?.consumption_street || "",
        // Iter43.5 — Backoffice & Workflow SF
        bo_received_at: sale.bo_received_at || "",
        problem_reason_code: sale.problem_reason_code || "",
        requires_rework: !!sale.requires_rework,
        returned: !!sale.returned,
        return_date: sale.return_date || "",
        return_reason: sale.return_reason || "",
        uploaded_flag: !!sale.uploaded_flag,
        uploaded_at: sale.uploaded_at || "",
        qc_call_date: sale.qc_call_date || "",
        qc_operator_name: sale.qc_operator_name || "",
        quality_call_outcome: sale.quality_call_outcome || "",
        cancellation_reason_text: sale.cancellation_reason_text || "",
        rejection_subreason: sale.rejection_subreason || "",
        // Workflow SF — Contract supplier-level
        business_status: sale.contract?.business_status || "",
        supplier_status: sale.contract?.supplier_status || "",
        supplier_ok: !!sale.contract?.supplier_ok,
        supplier_ko: !!sale.contract?.supplier_ko,
        supplier_status_date: sale.contract?.supplier_status_date || "",
        // ContractStatus (sale.contract_status_data)
        salesforce_contract_state: sale.contract_status?.salesforce_contract_state || "",
        salesforce_status_date: sale.contract_status?.salesforce_status_date || "",
        cs_cancellation_reason: sale.contract_status?.cancellation_reason || "",
        cs_cancellation_details: sale.contract_status?.cancellation_details || "",
        cs_quality_call_outcome: sale.contract_status?.quality_call_outcome || "",
        cs_quality_call_date: sale.contract_status?.quality_call_date || "",
        // Iter43.6 — Audit & Ownership (read-only display)
        sf_id: sale.sf_id || "",
        sf_agent_ref_id: sale.sf_agent_ref_id || "",
        commercial_agent_id: sale.commercial_agent_id || "",
        raw_agent_full_name: sale.raw_agent_full_name || "",
        import_source: sale.import_source || "",
        import_batch_id: sale.import_batch_id || "",
        first_imported_at: sale.first_imported_at || "",
        reconciliation_status: sale.reconciliation_status || "",
        agent_match_confidence: sale.agent_match_confidence || "",
        ownership_source_of_truth: sale.ownership_source_of_truth || "",
        ownership_override_flag: !!sale.ownership_override_flag,
        activation_pending_since: sale.activation_pending_since || "",
        crm_internal_id: sale.crm_internal_id || "",
        external_legacy_id: sale.external_legacy_id || "",
        sales_origin: sale.sales_origin || "",
        sales_unit_external_name: sale.sales_unit_external_name || "",
        last_synced_from_sf_at: sale.contract?.last_synced_from_sf_at || "",
        // Iter43.6 — Comisionare
        commission_month: sale.commission_month || "",
        commissioned: !!sale.commissioned,
        commissioned_at: sale.commissioned_at || "",
        report_month: sale.report_month || "",
        signing_month: sale.signing_month || "",
        // Iter43.6 — Contract date/agency
        agency: sale.contract?.agency || sale.contract?.agency_id || "",
        created_at: sale.contract?.created_at || "",
        // Sale data
        channel: sale.channel || "",
        agent_name: sale.agent_name || "",
        agent_code: sale.agent_code || "",
        bo_operator_name: sale.bo_operator_name || "",
        notes_agent: sale.notes_agent || "",
        // Status
        internal_status: sale.status || "Draft"
      }));
      
      if (sale.client) {
        setSelectedClient(sale.client);
        setFormData(prev => {
          const merged = {
            ...prev,
            client_type: sale.client.client_type || "PF",
            cnp: sale.client.cnp || "",
            cui: sale.client.cui || "",
            first_name: sale.client.first_name || "",
            last_name: sale.client.last_name || "",
            company_name: sale.client.company_name || "",
            phone_mobile: sale.client.phone_mobile || "",
            email: sale.client.email || "",
            county: sale.client.county || "",
            locality: sale.client.locality || "",
            street: sale.client.street || ""
          };
          // Iter48 — snapshot fresh state (within same React reconciliation tick)
          lastSavedSnapshotRef.current = JSON.stringify(merged);
          return merged;
        });
        setShowNewClient(false);
      } else {
        // Iter48 — no client to merge; snapshot whatever the previous setFormData produced
        setFormData(prev => {
          lastSavedSnapshotRef.current = JSON.stringify(prev);
          return prev;
        });
      }
    } catch (error) {
      toast.error("Eroare la încărcarea vânzării");
      navigate("/sales");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error("Failed to fetch statuses");
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies");
    }
  };

  const fetchDistributionZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/distribution-zones`);
      setDistributionZones(response.data);
    } catch (error) {
      console.error("Failed to fetch distribution zones");
    }
  };

  const fetchSalesChannels = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-channels`);
      setSalesChannels(response.data);
    } catch (error) {
      console.error("Failed to fetch sales channels");
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/offers`, { params: { active_only: true } });
      setOffers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch offers");
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Failed to fetch suppliers");
    }
  };

  const fetchCommercialAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/agents`, { params: { limit: 500 } });
      setCommercialAgents(response.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch agents");
    }
  };

  useEffect(() => {
    fetchStatuses();
    fetchAgencies();
    fetchDistributionZones();
    fetchSalesChannels();
    fetchSuppliers();
    fetchOffers();
    if (isAdminOrBO) fetchCommercialAgents();
    if (isEdit) {
      fetchSale();
    }
  }, [id]);

  // Iter48 — Initial snapshot is set inside fetchSale (after both setFormData calls).
  // isDirty: shallow JSON compare vs. last persisted snapshot
  const isDirty = isEdit && lastSavedSnapshotRef.current !== null
    && lastSavedSnapshotRef.current !== JSON.stringify(formData);

  // Iter48 — Browser-level guard (tab close / hard refresh)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleChange = (field, value) => {
    if (field === "commodity") commodityTouchedRef.current = true;
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === "identity_resolution" && value !== prev.identity_resolution) {
        // Iter67d — o operațiune comercială NOUĂ primește o cheie nouă; aceeași operațiune păstrează cheia la retry/dublu-click
        next.sale_id = "";
        next.operation_key = String(value || "").startsWith("link_new_sale:") ? (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`) : "";
      }
      if (field === "offer_id") {
        const off = offers.find((o) => o.id === value);
        next.product_kind = off?.product_kind || "";
        if (off && !prev.product_name) next.product_name = off.name;
      }
      return next;
    });
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      client_type: client.client_type || "PF",
      cnp: client.cnp || "",
      cui: client.cui || "",
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      company_name: client.company_name || "",
      phone_mobile: client.phone_mobile || "",
      email: client.email || "",
      county: client.county || "",
      locality: client.locality || "",
      street: client.street || ""
    }));
    setShowNewClient(false);
    setClientSearchOpen(false);
  };

  const getClientDisplayName = (client) => {
    if (!client) return "";
    if (client.client_type === "PF") {
      return client.full_name || `${client.last_name || ""} ${client.first_name || ""}`.trim() || client.cnp || "Client PF";
    }
    return client.company_name || client.cui || "Client PJ";
  };

  const handleSubmit = async (e, overrides = null) => {
    e?.preventDefault?.();
    const fd = overrides ? { ...formData, ...overrides } : formData;  // Iter67d — retrimitere cu sale_id / link_create_sale din dialog
    const isLink = !isEdit && String(fd.identity_resolution || "").startsWith("link");
    // Validation — Iter67d: reutilizarea unui contract existent NU cere client nou/ofertă (nu modifică oferta contractului)
    if (!isEdit && !isLink && !fd.offer_id) {
      toast.error("Selectează oferta (tipul produsului se completează din ofertă)");
      return;
    }
    if (!isEdit && String(fd.identity_resolution || "").startsWith("link_new_sale:") && (!fd.new_sale_purpose.trim() || fd.justification.trim().length < 10)) {
      toast.error("Operațiune comercială distinctă: completează scopul și justificarea (min. 10 caractere)");
      return;
    }
    if (showNewClient && !isLink) {
      if (!formData.county || !formData.locality || !formData.street) {
        toast.error("Județul, localitatea și strada sunt obligatorii");
        return;
      }
      if (formData.client_type === "PF" && !formData.cnp) {
        toast.error("CNP-ul este obligatoriu pentru PF");
        return;
      }
      if (formData.client_type === "PJ" && !formData.cui) {
        toast.error("CUI este obligatoriu pentru PJ");
        return;
      }
    } else if (!showNewClient && !selectedClient) {
      toast.error("Selectează un client");
      return;
    }
    
    setSaving(true);
    
    try {
      if (isEdit) {
        // For edit, update the sale and contract separately
        // First get existing sale to get contract_id
        const saleResponse = await axios.get(`${API_URL}/sales/${id}`);
        const existingSale = saleResponse.data;

        // Iter46 — Update Client inline (datele clientului devin editabile din SaleForm)
        if (selectedClient?.id) {
          const clientPayload = {
            client_type: formData.client_type,
            // CNP/CUI sunt fixate la EDIT (disabled în UI), dar le includem ca să nu fie golite
            cnp: formData.cnp || null,
            cui: formData.cui || null,
            first_name: formData.first_name || null,
            last_name: formData.last_name || null,
            company_name: formData.company_name || null,
            phone_mobile: formData.phone_mobile || null,
            email: formData.email || null,
            county: formData.county || null,
            locality: formData.locality || null,
            street: formData.street || null,
          };
          const cleanedClient = flac.stripNonWritableMulti
            ? flac.stripNonWritableMulti(clientPayload)
            : clientPayload;
          try {
            await axios.put(`${API_URL}/clients/${selectedClient.id}`, cleanedClient);
          } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.code === "flac_write_denied") {
              toast.error(`Câmpuri Client blocate de FLAC: ${detail.message}`);
              throw err;
            }
            // Iter49 — duplicate identity is BLOCKING (user must fix CNP/CUI)
            if (detail?.code === "duplicate_identity") {
              throw err;
            }
            // Non-blocking pe alte erori (de ex. 403 când agent nu poate edita client)
            console.warn("Client update skipped:", err?.response?.data || err?.message);
          }
        }

        // Update contract status if changed
        if (existingSale.contract_id) {
          await axios.put(`${API_URL}/contracts/${existingSale.contract_id}/status`, {
            internal_status: formData.internal_status
          });

          // Iter43.4 — Generic Contract field update (Contract & Loc consum tab)
          const contractPayload = {
            // Identity
            contract_number: formData.contract_number || null,
            contract_number_old: formData.contract_number_old || null,
            commercial_product_name: formData.commercial_product_name || null,
            contract_type: formData.contract_type || null,
            customer_category: formData.customer_category || null,
            offer_type: formData.offer_type || null,
            raw_product_name: formData.raw_product_name || null,
            product_name: formData.product_name || null,
            // Commercial / lifecycle
            is_bundle: formData.is_bundle,
            contract_duration_months: formData.contract_duration_months ? parseInt(formData.contract_duration_months, 10) : null,
            price_kwh: formData.price_kwh ? parseFloat(formData.price_kwh) : null,
            price_kwh_gas: formData.price_kwh_gas ? parseFloat(formData.price_kwh_gas) : null,
            estimated_annual_kwh: formData.estimated_annual_kwh ? parseFloat(formData.estimated_annual_kwh) : null,
            previous_supplier: formData.previous_supplier || null,
            distributor_name: formData.distributor_name || null,
            distribution_zone: formData.distribution_zone || null,
            signed_date: formData.signed_date || null,
            contract_date: formData.contract_date || null,
            start_date: formData.start_date || null,
            activation_date: formData.activation_date || null,
            cancellation_date: formData.cancellation_date || null,
            csd_date: formData.csd_date || null,
            // Loc consum
            pod: formData.pod || null,
            nlc: formData.nlc || null,
            nlc_gaz: formData.nlc_gaz || null,
            pdr: formData.pdr || null,
            clc: formData.clc || null,
            numar_loc_consum: formData.numar_loc_consum || null,
            tip_loc_consum: formData.tip_loc_consum || null,
            consumption_county: formData.consumption_county || null,
            consumption_locality: formData.consumption_locality || null,
            consumption_street: formData.consumption_street || null,
            // Iter43.5 — Workflow SF (supplier-level)
            business_status: formData.business_status || null,
            supplier_status: formData.supplier_status || null,
            supplier_ok: formData.supplier_ok,
            supplier_ko: formData.supplier_ko,
            supplier_status_date: formData.supplier_status_date || null,
          };
          // FLAC: strip fields user can't write
          const cleanedContract = flac.stripNonWritableMulti
            ? flac.stripNonWritableMulti(contractPayload)
            : contractPayload;
          try {
            await axios.put(`${API_URL}/contracts/${existingSale.contract_id}`, cleanedContract);
          } catch (err) {
            // Surface FLAC denials (403) gracefully — other validation errors propagate
            const detail = err?.response?.data?.detail;
            if (detail?.code === "flac_write_denied") {
              toast.error(`Câmpuri blocate de FLAC: ${detail.message}`);
              throw err;
            }
            throw err;
          }

          // Iter43.5 — ContractStatus update (salesforce_contract_state, quality_call_*, cancellation_*)
          const csPayload = {
            internal_status: formData.internal_status,
            salesforce_contract_state: formData.salesforce_contract_state || null,
            salesforce_status_date: formData.salesforce_status_date || null,
            quality_call_outcome: formData.cs_quality_call_outcome || null,
            quality_call_date: formData.cs_quality_call_date || null,
            cancellation_reason: formData.cs_cancellation_reason || null,
            cancellation_details: formData.cs_cancellation_details || null,
          };
          try {
            await axios.put(`${API_URL}/contracts/${existingSale.contract_id}/status`, csPayload);
          } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.code === "flac_write_denied") {
              toast.error(`Câmpuri status blocate de FLAC: ${detail.message}`);
              throw err;
            }
            // Don't throw on legacy status updates — old endpoint accepts more loosely
            console.warn("ContractStatus update warning", err);
          }
        }

        // Iter43.5 — Sale-level Backoffice & Workflow fields (PUT /api/sales/{id})
        const salePayload = {
          notes_bo: formData.notes_bo || null,
          notes_agent: formData.notes_agent || null,
          bo_received_at: formData.bo_received_at || null,
          bo_verified: !!formData.bo_verified,
          bo_operator_name: formData.bo_operator_name || null,
          problems: !!formData.problems,
          problems_description: formData.problems_description || null,
          problem_reason_code: formData.problem_reason_code || null,
          requires_rework: !!formData.requires_rework,
          returned: !!formData.returned,
          return_date: formData.return_date || null,
          return_reason: formData.return_reason || null,
          uploaded_flag: !!formData.uploaded_flag,
          uploaded_at: formData.uploaded_at || null,
          qc_call_date: formData.qc_call_date || null,
          qc_operator_name: formData.qc_operator_name || null,
          quality_call_outcome: formData.quality_call_outcome || null,
          cancellation_reason_text: formData.cancellation_reason_text || null,
          rejection_subreason: formData.rejection_subreason || null,
        };
        const cleanedSale = flac.stripNonWritableMulti
          ? flac.stripNonWritableMulti(salePayload)
          : salePayload;
        try {
          await axios.put(`${API_URL}/sales/${id}`, cleanedSale);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (detail?.code === "flac_write_denied") {
            toast.error(`Câmpuri Sale blocate de FLAC: ${detail.message}`);
            throw err;
          }
          throw err;
        }

        toast.success("Vânzare actualizată cu succes!");
        // Iter48 — refresh snapshot and flash green tick for 2s
        lastSavedSnapshotRef.current = JSON.stringify(formData);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } else {
        // For new sale, use quick-sale endpoint
        const payload = {
          // Client
          client_type: fd.client_type,
          cnp: fd.cnp || null,
          cui: fd.cui || null,
          first_name: fd.first_name || null,
          last_name: fd.last_name || null,
          company_name: fd.company_name || null,
          phone_mobile: fd.phone_mobile || null,
          email: fd.email || null,
          county: fd.county || null,
          locality: fd.locality || null,
          street: fd.street || null,
          // Contract
          pod: fd.pod || null,
          identity_resolution: fd.identity_resolution || null,  // Iter67
          // Iter67d — ofertă/tip produs (vânzare nouă) + rezolvarea completă a reutilizării
          offer_id: isLink ? null : (fd.offer_id || null),
          product_kind: isLink ? null : (fd.product_kind || null),
          sale_id: fd.sale_id || null,
          new_sale_purpose: fd.new_sale_purpose || null,
          justification: fd.justification || null,
          operation_key: fd.operation_key || null,
          easy_contract_id: fd.easy_contract_id || null,
          sf_contract_id: fd.sf_contract_id || null,
          nlc: fd.nlc || null,
          commodity: isLink && !commodityTouchedRef.current ? null : fd.commodity,
          product_name: fd.product_name || null,
          distribution_zone: fd.distribution_zone || null,
          estimated_annual_kwh: fd.estimated_annual_kwh ? parseFloat(fd.estimated_annual_kwh) : null,
          signed_date: fd.signed_date || null,
          previous_supplier: fd.previous_supplier || null,
          // Sale
          channel: fd.channel || null,
          agent_name: fd.agent_name || null,
          agent_code: fd.agent_code || null,
          commercial_agent_id: fd.commercial_agent_id || null,
          bo_operator_name: fd.bo_operator_name || null,
          notes_agent: fd.notes_agent || null,
          // Status
          internal_status: fd.internal_status
        };
        
        // Iter40b — drop fields user can't write (multi-entity payload)
        const cleanedPayload = flac.stripNonWritableMulti
          ? flac.stripNonWritableMulti(payload)
          : payload;
        const resp = await axios.post(`${API_URL}/sales/quick`, cleanedPayload);
        if (resp.data?.reused_sale) {
          toast.success(resp.data.message || "Vânzarea existentă a fost reutilizată (nicio vânzare nouă).", { duration: 8000 });
        } else if (resp.data?.linked_existing_contract) {
          toast.success(resp.data.idempotent ? "Operațiunea fusese deja înregistrată — aceeași vânzare (idempotent)."
            : resp.data.mode === "link_create_sale" ? "Vânzare creată pe contractul existent (contractul nu a fost modificat)."
            : "Vânzare nouă înregistrată pe contractul existent (operațiune comercială distinctă).", { duration: 8000 });
        } else {
          toast.success("Vânzare creată cu succes!");
        }
        navigate("/sales");
      }
    } catch (error) {
      // Iter49 — handle structured 409 duplicate_identity payload (from client PUT step)
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "pod_has_contracts") {
        toast.error(detail.message, { duration: 10000 });
        setLoading?.(false);
        return;
      }
      // Iter67d — rezolvarea explicită a reutilizării: mai multe vânzări / contract fără vânzare
      if (detail && typeof detail === "object" && (detail.code === "multiple_sales" || detail.code === "no_sale_for_contract")) {
        setLinkPrompt({ code: detail.code, sales: detail.sales || [], contractId: detail.contract_id || String(fd.identity_resolution).split(":")[1], message: detail.message });
        return;
      }
      if (detail && typeof detail === "object" && (detail.code === "identity_contradiction" || detail.code === "operation_key_conflict")) {
        toast.error(detail.message, { duration: 12000 });
        return;
      }
      if (detail && typeof detail === "object" && detail.code === "duplicate_identity") {
        toast.error(detail.message, {
          duration: 8000,
          action: detail.existing_client_id ? {
            label: "Deschide clientul existent",
            onClick: () => navigate(`/clients/${detail.existing_client_id}`),
          } : undefined,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Eroare la salvarea vânzării"));
      }
    } finally {
      setSaving(false);
    }
  };

  // Get distribution zones based on selected commodity
  const getDistributionZonesForCommodity = () => {
    if (formData.commodity === "electric") {
      return distributionZones.electric || distributionZones.zones || [];
    }
    if (formData.commodity === "gas") {
      return distributionZones.gas || distributionZones.zones || [];
    }
    return [...(distributionZones.electric || []), ...(distributionZones.gas || [])];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/sales">
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {isEdit ? "Editează Vânzare" : "Vânzare Nouă"}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? "Actualizează datele vânzării" : "Completează datele pentru o nouă vânzare"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Iter40b — FLAC scope banner */}
        <FlacScopeBanner flac={flac} entity="acest formular vânzare" />
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {/* Iter53 P47 — Provider expune formData/handleChange/canWrite/isHidden/isEdit
              către toate tab-urile extrase (Detalii/Contract/Backoffice/Audit). */}
          <SaleFormProvider
            formData={formData}
            handleChange={handleChange}
            canWrite={canWrite}
            isHidden={isHidden}
            isEdit={isEdit}
            details={{
              selectedClient, setSelectedClient,
              showNewClient, setShowNewClient,
              clientSearchOpen, setClientSearchOpen,
              clientSearchTerm, setClientSearchTerm,
              clientSearchResults,
              searchingClients,
              duplicates,
              handleSelectClient,
              getClientDisplayName,
              suppliers,
              salesChannels,
              statuses,
              getDistributionZonesForCommodity,
              isAdminOrBO,
              commercialAgents,
              offers,
            }}
          >
          <TabsList className="grid w-full grid-cols-4" data-testid="sale-form-tabs">
            <TabsTrigger value="details" data-testid="tab-form-details">Detalii vânzare</TabsTrigger>
            <TabsTrigger value="contract" data-testid="tab-form-contract">Contract &amp; Loc consum</TabsTrigger>
            <TabsTrigger value="backoffice" data-testid="tab-form-backoffice">Backoffice &amp; Workflow</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-form-audit">Audit &amp; Tehnice</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-6 space-y-6" data-testid="tab-form-details-content">
            {/* Iter53 P47 — Details tab extracted to component (uses SaleFormContext) */}
            <SaleFormDetailsTab />
          </TabsContent>

          {/* Iter43.4 — Tab Contract & Loc consum (paritate EDIT cu catalog FLAC) */}
          <TabsContent value="contract" className="mt-6 space-y-6" data-testid="tab-form-contract-content">
            {/* Iter53 P47 — Contract tab extracted to component (uses SaleFormContext) */}
            <SaleFormContractTab />
          </TabsContent>

          {/* Iter43.5 — Tab Backoffice & Workflow SF (paritate EDIT) */}
          <TabsContent value="backoffice" className="mt-6 space-y-6" data-testid="tab-form-backoffice-content">
            {/* Iter53 P46 — extras într-o componentă care consumă SaleFormContext */}
            {!isEdit && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Aceste câmpuri se completează după salvarea vânzării (pagina de Editare).
              </div>
            )}
            <SaleFormBackofficeTab />
          </TabsContent>

          {/* Iter43.6 — Tab Audit & Tehnice (read-only/disabled system-managed fields) */}
          <TabsContent value="audit" className="mt-6 space-y-6" data-testid="tab-form-audit-content">
            {/* Iter53 P37 — extras într-un component read-only self-contained.
                Toate câmpurile sunt disabled — depind DOAR de `formData`. */}
            <SaleFormAuditTab formData={formData} />
          </TabsContent>
          </SaleFormProvider>
        </Tabs>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          {/* Iter48 — Dirty/saved indicator */}
          {isEdit && (
            <span className="text-sm flex items-center gap-1.5" data-testid="dirty-status">
              {justSaved ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid="just-saved-icon" />
                  <span className="text-emerald-700">Salvat</span>
                </>
              ) : isDirty ? (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" data-testid="dirty-dot" />
                  <span className="text-amber-700">Modificări nesalvate</span>
                </>
              ) : null}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid="cancel-btn"
            onClick={(e) => {
              if (isDirty && !window.confirm("Aveți modificări nesalvate. Părăsiți pagina fără să salvați?")) {
                e.preventDefault();
                return;
              }
              navigate("/sales");
            }}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            disabled={saving || (isEdit && !isDirty)}
            data-testid="save-btn"
            className={isDirty && isEdit ? "ring-2 ring-amber-400 ring-offset-1" : ""}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Se salvează...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEdit ? "Actualizează" : "Salvează"}
              </>
            )}
          </Button>
        </div>
      </form>
      <LinkResolutionDialog
        prompt={linkPrompt}
        onClose={() => setLinkPrompt(null)}
        onPickSale={(saleId) => { setFormData((p) => ({ ...p, sale_id: saleId })); setLinkPrompt(null); handleSubmit(null, { sale_id: saleId }); }}
        onConfirmCreate={(contractId) => { const ir = `link_create_sale:${contractId}`; setFormData((p) => ({ ...p, identity_resolution: ir, operation_key: "" })); setLinkPrompt(null); handleSubmit(null, { identity_resolution: ir, operation_key: "" }); }}
      />
    </div>
  );
};

export default SaleForm;
